
import { getCookieValue } from "@/utils/CookieHelper"
import User, { UserIdWithStatus, UserIdWithStatusAndFields, UserPreferences, UserWithFields, AccountDetails, AuthSetup, AuthSubmit, ExtendedPermission, LabeledValue, CurrentUser } from "@/types/user"
import axios, { AxiosInstance, AxiosProgressEvent } from "axios"
import store from "@/store"

import {
	Case, CaseDetails, Field, Template, TemplateDetails,
	DataDefinition,
	DataDefinitionWithFields,
	Area,
	Team,
	AreaDetails,
	TreeNode,
	FieldSelectResult,
	CaseAudit,
	DataSourceItem,
	Rule,
	Status,
	Severity,
	TimeZone,
	CaseSummary,
	CaseEmailContact,
	CaseEmailRequest
} from "@/types"
import { ViewFullSummary } from "@/types/views/viewSummary"

import CaseFile from "@/types/caseFile"
import { LoginRequest, PasswordResetRequest, PasswordChangeRequest, LoginResult } from "@/types/loginRequest"
import { PagedData } from "@/types/pagedData"
import { SearchClause } from "@/types/searchFilter"
import { Tag } from "@/types/Tag"
import { Schedule, ScheduleQueue } from "@/types/schedule"
import SignUpForm from "@/types/signupForm"
import { DataType, DynamicParameter, DynamicParameterValue, Insight, InsightDetails, InsightSummary } from "@/types/insights"
import { RecordNameSettings } from "@/types/recordNameSettings"
import { CaseNameSettings } from "@/types/caseNameSettings"
import { MergeCaseFields } from "@/types/mergeFieldTypes"
import { Folder, FolderType } from "@/types/folder"
import { UserSettings } from "@/types/userSettings"
import { IntegrationOutOption } from "@/types/integration"
import { CalculatedResults, CalculationResults } from "@/types/insights/calculatedResults"
import { DataSource } from "@/types/dataSourceItem"
import { Clause } from "@/types/view"
import { GetCasesForViewRequest } from "@/types/paginatedRequest"
import { CasesTables } from "@/types/case"
import { AreaNodeDto } from "@/types/areaNodeDto"
import { GetInsightByIdRequest } from "@/types/insights/insightRequests"

export enum HttpStatusCode {
	OK = 200,
	Created = 201,
	BadRequest = 400,
	Unauthorized = 401,
	SubscriptionInvalid = 402,
	NotFound = 404,
	TokenExpired = 498
}

export enum XHRReadyState {
	Unsent = 0,
	Opened = 1,
	HeadersReceived = 2,
	Loading = 3,
	Done = 4
}

class Api {
	private axios: AxiosInstance;

	constructor () {
		this.axios = axios.create({
			baseURL: process.env.VUE_APP_HTTP_API_URI,
			withCredentials: true,
			withXSRFToken: true,
			headers: {
				Accept: "application/json, text/javascript, */*; q=0.01",
				"Content-Type": "application/json"
			}
		})

		this.axios.interceptors.response.use(undefined, (error: any) => {
			if (error.response) {
				let errorMessage =
					error.response.data && error.response.data.length > 2
						? error.response.data
						: ""

				// If User has a failed request, to account for accessing a url without login, store it for the API to access.
				if (!window.location.href.includes("login")) {
					document.cookie = `attemptedUrl= ${window.location.href}`
				}

				switch (error.response.status) {
				// 401 - Unauthorized error
					case HttpStatusCode.Unauthorized: {
						console.log("Logging out due to 401")
						store.dispatch("logOut", true).then()
						break
					}
					// 402 - Payment required thrown when subscriptions are invalid.
					case HttpStatusCode.SubscriptionInvalid: {
						console.log("Invalid subscription detected, redirecting user to organization settings page")
						store.dispatch("subscriptionInvalid", true).then()
						break
					}
					// 498 - Token expired
					case HttpStatusCode.TokenExpired: {
						console.log("Logging out due to Anti-Forgery")
						store.dispatch("logOut", true).then()
						break
					}
				}

				return Promise.reject(errorMessage)
			} else {
				return Promise.reject(new Error("Unable to connect"))
			}
		})
	}

	public getAxios (): any { return this.axios }

	public get baseUrl (): string {
		return process.env.VUE_APP_HTTP_API_URI || ""
	}

	public pictureUrl (userId: string): string {
		return `${process.env.VUE_APP_HTTP_API_URI}users/picture/${userId}`
	}

	public async clientIds (): Promise<{ [type: string]: string }> {
		return (await this.axios.get("/oauth/clientIds")).data
	}

	public async login (data: LoginRequest): Promise<LoginResult | null> {
		return (await this.axios.post("/login", data)).data
	}
	public async requestPasswordReset (data: PasswordResetRequest) {
		await this.axios.post("/login/sendResetPassword", data)
	}
	public async resetPassword (data: PasswordChangeRequest) {
		await this.axios.post("/login/resetPassword", data)
	}
	public async logout () {
		await this.axios.get("/login/logout")
	}

	public async accountDetails (userId: string | null = null): Promise<AccountDetails> {
		return userId
			? (await this.axios.get(`/users/userAccount/${userId}`)).data
			: (await this.axios.get("/users/userAccount")).data
	}

	public async setMfa (mfaParams: AuthSetup) {
		await this.axios.put("/users/mfa", mfaParams)
	}

	public async setSystemMfa (mfaParams: AuthSetup) {
		await this.axios.put("/users/mfa/admin", mfaParams)
	}

	public async removeMfa (mfaParams: AuthSubmit) {
		await this.axios.post("/users/mfa/remove", mfaParams)
	}

	public async removeMfaForUser (userId: string) {
		await this.axios.post(`/users/mfa/remove/${userId}`)
	}

	public async submitMfa (mfaParams: AuthSubmit) {
		await this.axios.post("/login/mfa", mfaParams)
	}

	public async submitSystemMfa (mfaParams: AuthSubmit) {
		await this.axios.post("/login/mfa/admin", mfaParams)
	}

	public async me (): Promise<CurrentUser> {
		return (await this.axios.get("/users/me")).data
	}

	public async adEnabled (): Promise<boolean> {
		return (await this.axios.get("/users/adEnabled")).data
	}

	public async updateUserPreferences (userPreferences: UserPreferences): Promise<boolean> {
		return (await this.axios.post("/users/updatePreferences", userPreferences)).status === HttpStatusCode.OK
	}

	public async allSearch (searchTerm: string, includeUsers: boolean = true): Promise<any[]> {
		return (await this.axios.get("/search/simple", {
			params: {
				searchTerm: searchTerm,
				includeUsers: includeUsers
			}
		})).data
	}
	public async searchAdvanced (searchType: string, searchTerm: string, searchClauses: SearchClause[], pageNumber: number): Promise<PagedData<Case>> {
		const payload = {
			searchTerm,
			clauses: searchClauses
		}

		return (await this.axios.post(`/search/advanced/${searchType}`, payload, {
			params: {
				pageNumber
			}
		})).data
	}

	public async caseViews (viewId: string | null, searchTerm: string = "", id: string = ""): Promise<ViewFullSummary> {
		return (await this.axios.get("/views/summary", {
			params: {
				activeViewId: viewId,
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				searchTerm: searchTerm,
				id: id
			}
		})).data
	}

	public async casesForView (request: GetCasesForViewRequest): Promise<PagedData<CaseDetails>> {
		return (await this.axios.get("/views/cases", {
			params: {
				viewId: request.viewId,
				timeZone: request.timeZone,
				page: request.page,
				pageSize: request.pageSize,
				searchTerm: request.searchTerm ?? "",
				sortBy: request.sortBy,
				sortDesc: request.sortDesc,
				sortDataType: request.sortDataType,
				tableId: request.tableId,
				id: request.id
			}
		})).data
	}

	public async caseViewsGet () {
		let data = await this.axios.get("/views")
		return data.data
	}

	public async caseUpdateView (view: any) {
		await this.axios.post("/views/updateView", view)
	}

	public async caseDeleteView (view: any) {
		await this.axios.delete(`/views/${view.id}`)
	}

	public async foldersGet (folderType: FolderType): Promise<Folder[]> {
		return (await this.axios.get("/folders", {
			params: {
				folderType
			}
		})).data
	}

	public async folderSet (folder: Folder): Promise<Folder> {
		return (await this.axios.put("/folders", folder)).data
	}

	public async folderDelete (folder: Folder): Promise<void> {
		this.axios.delete(`/folders/${folder.id}`)
	}

	public async folderUsage (folder: Folder): Promise<{ id: string, label: string }[]> {
		return (await this.axios.get("/folders/usage", {
			params: {
				folderType: folder.folderType,
				title: folder.title
			}
		})).data
	}

	public async caseList (
		viewId: string | null = null,
		pageNumber: number = 1,
		pageSize: number = 20,
		cacheId: string = "",
		searchTerm: string = ""
	): Promise<CasesTables> {
		const response = await this.axios.get("/cases/list", { params: {
			viewId,
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			pageSize,
			pageNumber,
			cacheId,
			searchTerm
		} })
		return response.data
	}

	public async caseListTable (
		request: GetCasesForViewRequest
	): Promise<CasesTables> {
		return (await this.axios.get("/cases/table", {
			params: {
				viewId: request.viewId,
				timeZone: request.timeZone,
				page: request.page,
				pageSize: request.pageSize,
				searchTerm: request.searchTerm ?? "",
				sortBy: request.sortBy,
				sortDesc: request.sortDesc,
				sortDataType: request.sortDataType,
				tableId: request.tableId,
				id: request.id
			}
		})).data
	}

	public async caseDetails (caseNumber: string): Promise<CaseDetails> {
		return (await this.axios.get("/cases/details", { params: { caseNumber: caseNumber } })).data
	}

	public async casesBetween (start: Date, end: Date): Promise<CaseSummary[]> {
		return (await this.axios.get("/cases/between", {
			params: {
				start: start.toISOString(),
				end: end.toISOString()
			}
		})).data
	}

	public async saveCase (caseDetails: CaseDetails): Promise<Case> {
		const caseId = (await this.axios.post("/cases", caseDetails)).data
		return caseId
	}

	public async recordToCase (caseId: string) {
		await this.axios.put(`/cases/makeCase/${caseId}`)
	}

	public async markAsSensitive (caseId: string) {
		await this.axios.put(`/cases/markSensitive/${caseId}`)
	}

	public async close (caseId: string) {
		await this.axios.get(`/cases/close/${caseId}`)
	}

	public async deleteCase (caseId: string) {
		await this.axios.delete(`/cases/delete/${caseId}`)
	}

	public async saveCaseProperty (caseId: string, propertyName: string, value: string | null): Promise<void> {
		await this.axios.post(`/cases/property/${caseId}/${propertyName}`, { value })
	}

	public async saveCaseField (caseId: string, fieldId: string, value: string | null, inProgressUpdate: boolean = false): Promise<void> {
		await this.axios.post(`/cases/field/${caseId}/${fieldId}/${inProgressUpdate}`, { value })
	}

	public async updateCaseDataField (caseId: string, fieldId: string, action: number, value: any): Promise<void> {
		await this.axios.post(`/cases/field/${caseId}/${fieldId}/item`, value, {
			params: {
				updateType: action
			}
		})
	}

	public async selectCaseField (caseId: string, fieldId: string, selected: boolean): Promise<FieldSelectResult> {
		return (await this.axios.put(`/cases/fieldselect/${caseId}/${fieldId}/${selected}`)).data
	}

	public async extendCaseFieldLock (caseId: string, fieldId: string): Promise<void> {
		return (await this.axios.post(`/cases/extendlock/${caseId}/${fieldId}`)).data
	}

	public async setFieldCalculated (caseId: string, fieldId: string, calculated: boolean): Promise<void> {
		await this.axios.put(`/cases/override-calculated/${caseId}/${fieldId}/${calculated}`)
	}

	public async replaceStatusOnCaseList (existingStatusId: string | null, replacementStatusId: string): Promise<void> {
		await this.axios.put(`/cases/replaceStatus/${existingStatusId}/${replacementStatusId}`)
	}

	public async replaceSeverityOnCaseList (existingSeverityId: string | null, replacementSeverityId: string): Promise<void> {
		await this.axios.put(`/cases/replaceSeverity/${existingSeverityId}/${replacementSeverityId}`)
	}

	public async mergeCases (fromCaseId: string, toCaseId: string, mergeCaseModel: MergeCaseFields): Promise<void> {
		await this.axios.post(`/cases/merge/${fromCaseId}/${toCaseId}`, mergeCaseModel)
	}

	public async areas (): Promise<TreeNode<Area>[]> {
		return (await this.axios.get("/areas")).data
	}

	public async paginatedAreas (pageNumber: number = 1, pageSize: number = 50, searchTerm: string | null = null): Promise<PagedData<TreeNode<Area>>> {
		return (await this.axios.get("/areas/paginated", { params: {
			pageNumber,
			pageSize,
			searchTerm
		}
		})).data
	}

	public async areaList (): Promise<Area[]> {
		return (await this.axios.get("/areas/list")).data
	}
	public async area (areaId: string): Promise<AreaDetails> {
		return (await this.axios.get(`/areas/${areaId}`)).data
	}
	public async areaSave (newArea: AreaDetails): Promise<Area> {
		return (await this.axios.post("/areas", newArea)).data
	}
	public async areaDelete (deleteArea: AreaDetails): Promise<void> {
		await this.axios.delete(`/areas/${deleteArea.id}`)
	}

	public async getAreaDictionary (): Promise<Map<string, string>> {
		const { data } = await this.axios.get("/areas/dictionary")
		return data
	}

	public async searchAreaTree (
		query: string
	): Promise<AreaNodeDto[]> {
		const response = await this.axios.get("/areas/tree/search", {
			params: { query }
		})
		return response.data
	}

	public async getAreaTreeNode (
		areaId: string
	): Promise<AreaNodeDto> {
		const response = await this.axios.get(
			`/areas/${areaId}`
		)
		return response.data
	}

	public async getFullAreaTree (): Promise<AreaNodeDto[]> {
		const response = await this.axios.get(
			"/areas/fullAreaTree"
		)
		return response.data
	}

	public async severities (): Promise<Severity[]> {
		return (await this.axios.get("/severity")).data
	}

	public async isSeverityInUse (severityId: string): Promise<boolean> {
		return (await this.axios.get(`/severity/${severityId}/in-use`)).data
	}

	public async statuses (): Promise<Status[]> {
		return (await this.axios.get("/status")).data
	}

	public async isStatusInUse (statusId: string): Promise<boolean> {
		return (await this.axios.get(`/status/${statusId}/in-use`)).data
	}

	public async recordNameSettings (): Promise<RecordNameSettings[]> {
		return (await this.axios.get("/recordNameSettings")).data
	}

	public async caseNameSettings (): Promise<CaseNameSettings[]> {
		return (await this.axios.get("/caseNameSettings")).data
	}

	public async categories (): Promise<Template[]> {
		return (await this.axios.get("/templates")).data
	}

	public async templatesWithFields () : Promise<any[]> {
		return (await this.axios.get("/templates/templatesWithFields")).data
	}

	public async categoryFields (categoryId: string): Promise<any[]> {
		return (await this.axios.get(`/templates/templateFields/${categoryId}`)).data
	}

	public async templateDetails (categoryId: string): Promise<TemplateDetails> {
		return (await this.axios.get(`/templates/details/${categoryId}`)).data
	}

	public async cadenceDetails (templateId: string): Promise<any> {
		return (await this.axios.get(`/templates/cadenceDetails/${templateId}`)).data
	}

	public async templateSave (template: TemplateDetails): Promise<string[]> {
		return (await this.axios.post("/templates", template)).data
	}
	public async templateDelete (template: TemplateDetails): Promise<string[]> {
		return (await this.axios.delete(`/templates/${template.id}`)).data
	}

	public async fields (): Promise<Field[]> {
		return (await this.axios.get("/fields")).data
	}
	public async fieldSave (field: Field): Promise<string> {
		return (await this.axios.post("/fields", field)).data
	}
	public async fieldUsage (field: Field): Promise<void> {
		return (await this.axios.get(`/fields/usage/${field.id}`)).data
	}
	public async fieldDelete (field: Field): Promise<void> {
		return (await this.axios.delete(`/fields/${field.id}`)).data
	}

	public async generateID (): Promise<string> {
		return (await this.axios.get("/fields/generateId")).data
	}
	public async rules (): Promise<Rule[]> {
		return (await this.axios.get("/rules")).data
	}
	public async ruleSave (rule: Rule): Promise<string> {
		return (await this.axios.post("/rules", rule)).data
	}
	public async ruleDelete (rule: Rule): Promise<void> {
		return (await this.axios.delete(`/rules/${rule.id}`)).data
	}

	public async definitions (): Promise<DataDefinition[]> {
		return (await this.axios.get("/dataSource/definition")).data
	}
	public async definitionDetails (dataDefinitionId: string): Promise<DataDefinitionWithFields> {
		return (await this.axios.get(`/dataSource/definition/${dataDefinitionId}`)).data
	}
	public async definitionDetailsForSource (dataSourceId: string): Promise<DataDefinitionWithFields> {
		return (await this.axios.get(`/dataSource/definition/forsource/${dataSourceId}`)).data
	}
	public async definitionSave (template: DataDefinitionWithFields): Promise<string[]> {
		return (await this.axios.post("/dataSource/definition", template)).data
	}
	public async definitionDelete (template: DataDefinitionWithFields): Promise<string[]> {
		return (await this.axios.delete(`/dataSource/definition/${template.id}`)).data
	}
	public async definitionUsage (definition: DataDefinition): Promise<any> {
		return (await this.axios.get(`/dataSource/definition/usage/${definition.id}`)).data
	}
	public async specialDefinitionId (definitionType: string): Promise<string> {
		return (await this.axios.get(`/dataSource/specialDefinitionId/${definitionType}`)).data
	}

	public async completeAction (caseId: string, actionId: string): Promise<void> {
		await this.axios.get(`/cases/action/${caseId}/${actionId}`)
	}
	public async history (caseId: string, greaterThanDate: string | null = null): Promise<CaseAudit[]> {
		return (await this.axios.get(`/cases/history/${caseId}`, {
			params: {
				greaterThanDate
			}
		})).data
	}

	public async caseFiles (caseId: string): Promise<CaseFile[]> {
		return (await this.axios.get(`/files/${caseId}`)).data
	}

	public async linkCase (caseId: string, linkToCase: string): Promise<CaseFile[]> {
		return (await this.axios.get(`/cases/link/${caseId}/${linkToCase}`)).data
	}

	public async caseIntegrationLinks (caseId: string): Promise<IntegrationOutOption[]> {
		return (await this.axios.get(`/cases/${caseId}/integrationLinks`)).data
	}

	public uploadFiles (caseId: string, fieldId: string | null, formData: FormData,
		progress: (e: ProgressEvent) => void,
		error: (errorMessage: string) => void,
		finished: () => void): void {
		var xhr = new XMLHttpRequest()
		xhr.withCredentials = true

		const url = `${process.env.VUE_APP_HTTP_API_URI}files/${caseId}${(fieldId ? "/" + fieldId : "")}`

		xhr.open("POST", url, true)
		this.setXsrfHeader(xhr)
		xhr.onreadystatechange = () => {
			if (xhr.readyState === XHRReadyState.Done) {
				if (xhr.status < HttpStatusCode.BadRequest) {
					// Every thing ok, file uploaded
					console.log(xhr.responseText) // handle response.
					finished()
				} else {
					if (xhr.responseText) {
						error(xhr.responseText)
					} else {
						error(xhr.status.toString())
					}
				}
			}
		}
		xhr.upload.onerror = (e: ProgressEvent) => {
			error("Failed to upload file")
		}
		xhr.upload.onprogress = progress

		xhr.send(formData)
	}

	public async deleteFile (fileId: string, fieldId: string | null): Promise<void> {
		return this.axios.delete(`/files/${fileId}/field${(fieldId ? "/" + fieldId : "")}`)
	}

	public async caseComments (caseId: string): Promise<any[]> {
		return (await this.axios.get(`/comments/${caseId}`)).data
	}

	public async caseTyping (caseId: string, typing: boolean): Promise<any[]> {
		return (await this.axios.get(`/comments/typing/${caseId}`, {
			params: {
				typing: typing ? "true" : "false"
			}
		})).data
	}

	public async postComment (caseId: string, comment: string, teamId: string | null, commentIsHtml: boolean = false): Promise<void> {
		await this.axios.post("/cases/update", {
			caseId,
			comment,
			isHtml: commentIsHtml
		})
	}

	public async teamsGet (): Promise<Team[]> {
		return (await this.axios.get("/teams")).data
	}

	public async teamSave (team: Team): Promise<Team> {
		return (await this.axios.post("/teams", team)).data
	}

	public async teamDelete (teamId: string): Promise<void> {
		return (await this.axios.delete(`/teams/${teamId}`)).data
	}

	public async extendedPermissionsGet (): Promise<ExtendedPermission[]> {
		return (await this.axios.get("/users/extendedPermissions")).data
	}

	public async dataSources (): Promise<DataSource[]> {
		return (await this.axios.get("/datasource")).data
	}

	public async dataSourceGet (dataSourceId: string): Promise<DataSource> {
		return (await this.axios.get(`/datasource/${dataSourceId}`)).data
	}

	public async dataSourceUsage (dataSource: any): Promise<any> {
		return (await this.axios.get(`/dataSource/usage/${dataSource.id}`)).data
	}

	public async dataSourceSave (dataSource: any): Promise<string[]> {
		return (await this.axios.post("/dataSource", dataSource)).data
	}
	public async dataSourceDelete (dataSource: any): Promise<string[]> {
		return (await this.axios.delete(`/dataSource/${dataSource.id}`)).data
	}

	public async dataSourceSearch (dataSourceId: string, fieldId: string | null, search: string, pageNumber: number = 1): Promise<PagedData<DataSourceItem>> {
		return (await this.axios.get(`/datasource/search/${dataSourceId}`, {
			params: {
				search,
				fieldId,
				pageNumber
			}
		})).data
	}

	public async dataSourceGetAll (dataSourceId: string, includeAll?: boolean): Promise<DataSourceItem[]> {
		return (await this.axios.get(`/datasource/item/all/${dataSourceId}`, {
			params: {
				includeAll: includeAll		// load all regardless of view permissions (for insights etc.)
			}
		})).data
	}

	public async dataSourceGetItem (dataSourceItemId: string) {
		return (await this.axios.get(`/datasource/item/${dataSourceItemId}`)).data
	}
	public async dataSourceItemGetLabel (definitionId: string, dataSourceItem: any): Promise<string> {
		return (await this.axios.post(`/dataSource/item/label/${definitionId}`, dataSourceItem)).data
	}
	public async dataSourceCreateItem (dataSourceItem: any) {
		return (await this.axios.post("/datasource/item", dataSourceItem)).data
	}

	public dataSourceUploadCsv (dataSourceId: string, formData: FormData,
		progress: (e: ProgressEvent) => void): Promise<any[]> {
		var xhr = new XMLHttpRequest()
		xhr.withCredentials = true

		const url = `${process.env.VUE_APP_HTTP_API_URI}dataSource/upload/${dataSourceId}`

		return new Promise((resolve, reject) => {
			xhr.open("POST", url, true)
			this.setXsrfHeader(xhr)
			xhr.onreadystatechange = () => {
				if (xhr.readyState === XHRReadyState.Done) {
					if (xhr.status < HttpStatusCode.BadRequest) {
						resolve(JSON.parse(xhr.responseText))
					} else {
						if (xhr.responseText) {
							reject(xhr.responseText)
						} else {
							reject(xhr.status.toString())
						}
					}
				}
			}
			xhr.upload.onerror = (e: ProgressEvent) => {
				reject(new Error("Failed to upload file"))
			}
			xhr.upload.onprogress = progress

			xhr.send(formData)
		})
	}

	public async dataSourceItemDelete (dataSourceItem: any) {
		return (await this.axios.delete(`/datasource/item/${dataSourceItem.id}`)).data
	}
	public async dataSourceItemSearchCases (fieldId: string, searchTerm: string, isIdField: boolean): Promise<Case[]> {
		return (await this.axios.get(`/dataSource/cases/field/${fieldId}`, {
			params: {
				searchTerm,
				searchTermIsId: isIdField
			}
		})).data
	}

	public async userDetails (userId: string, caseId: string = ""): Promise<any> {
		return (await this.axios.get(`/users/details/${userId}/${caseId}`)).data
	}

	public async userFieldDetails (userId: string): Promise<UserWithFields> {
		return (await this.axios.get(`/users/fieldDetails/${userId}`)).data
	}

	/**
	 * Get cases by current user
	 */
	public async userCases (userId: string, includeCollaborating: boolean, archived: boolean, pageNumber: number): Promise<PagedData<Case>> {
		return (await this.axios.get(`/users/cases/${userId}`, {
			params: {
				includeCollaborating,
				archived,
				pageNumber
			}
		})).data
	}

	public async addUser (caseId: string, userId: string) {
		await this.axios.put(`/cases/users/${caseId}/${userId}`)
	}

	public async removeUser (caseId: string, userId: string) {
		await this.axios.delete(`/cases/users/${caseId}/${userId}`)
	}

	public async assignUser (caseId: string, userId: string) {
		await this.axios.put(`/cases/assign/${caseId}/${userId}`)
	}

	public async addTeam (caseId: string, teamId: string) {
		await this.axios.put(`/cases/teams/${caseId}/${teamId}`)
	}

	public async searchUsers (searchTerm: string, teamId: string | null = null, teamIds: string[] | null = null) {
		return (await this.axios.get("/users/search", {
			params: {
				search: searchTerm,
				teamId,
				teams: teamIds
			}
		})).data
	}

	public async searchInvite (caseId: string, searchTerm: string, teams: string[] | null = null, allowTeams: boolean = true, disallowAdded: boolean = true) {
		return (await this.axios.get(`/users/searchForInvite/${caseId}`, {
			params: {
				search: searchTerm,
				teams,
				allowTeams,
				disallowAdded
			}
		})).data
	}

	public async usersGet (teamId: string | null = null): Promise<UserWithFields[]> {
		if (teamId) {
			return (await this.axios.get(`/users/team/${teamId}`)).data
		} else {
			return (await this.axios.get("/users/")).data
		}
	}

	public async userSave (user: UserWithFields): Promise<UserWithFields> {
		return (await this.axios.post("/users", user)).data
	}
	public async saveOwnFields (fieldValues: { [key: string]: LabeledValue }, finishIntroduction: boolean): Promise<void> {
		await this.axios.post("/users/ownFields", {
			fieldValues
		}, {
			params: {
				finishIntroduction
			}
		})
	}

	public async userDelete (userId: string): Promise<User> {
		return (await this.axios.delete(`/users/${userId}`)).data
	}

	public async userSpecialDefinition (): Promise<DataDefinitionWithFields> {
		return (await this.axios.get("/users/userSpecialDefinition")).data
	}

	public async userStatusListGet (userIds: string[]): Promise<UserIdWithStatus[]> {
		return (await this.axios.get("/users/statusList", {
			params: {
				userIds
			}
		})).data
	}

	public async userStatusListWithFieldsGet (userIds: string[]): Promise<UserIdWithStatusAndFields[]> {
		return (await this.axios.post("/users/statusWithFieldsList", userIds)).data
	}

	public async userSettingsGet (userId: string): Promise<UserSettings> {
		let response = await this.axios.get(`/users/settings/${userId}`)
		return response.data
	}

	public async userSettingsSave (userSettings: UserSettings): Promise<UserSettings> {
		let response = await this.axios.put("/users/settings", userSettings)
		return response.data
	}

	public async userSettingSaveOne (key: string, value: any): Promise<UserSettings> {
		let response = await this.axios.put("/users/setting", {
			[key]: value
		})
		return response.data
	}

	public async uploadUserImage (userId: string, formData: FormData, progress: (e: AxiosProgressEvent) => void): Promise<void> {
		await this.axios.post(`/users/picture/upload/${userId}`, formData, {
			headers: {
				"Content-Type": "multipart/form-data"
			},
			onUploadProgress: (event) => progress(event)
		})
	}

	public async removeUserImage (userId: string): Promise<void> {
		await this.axios.delete(`/users/picture/${userId}`)
	}

	public async hasUploadedPicture (userId: string): Promise<boolean> {
		return (await this.axios.get(`/users/picture/${userId}/exists`))
			.data
	}

	public async setUserStatus (userId: string, statusId: string): Promise<void> {
		await this.axios.post(`/users/setStatus/${userId}/${statusId}`)
	}

	public async clearUserStatus (userId: string): Promise<void> {
		await this.axios.post(`/users/clearStatus/${userId}`)
	}

	public async caseSearch (searchTerm: string, includeUsers:boolean = false): Promise<Case[]> {
		return (await this.axios.get("/search/simple", {
			params: {
				searchTerm: searchTerm,
				includeUsers: includeUsers
			}
		})).data
	}

	public async insightsGet (): Promise<InsightSummary[]> {
		return (await this.axios.get("/insights")).data
	}
	public async insightGet (id: string, includeImportedCalculations: boolean = false): Promise<InsightDetails> {
		return (await this.axios.get(`/insights/${id}`, {
			params: {
				includeImportedCalculations
			}
		})).data
	}
	public async insights (insightQuery: any): Promise<CalculatedResults> {
		return (await this.axios.post("/insights/calculated", insightQuery)).data
	}

	public async insightsCalculatedCasesForTable (insight: any, id: string): Promise<CalculationResults> {
		return (await this.axios.post(`/insights/calculatedTableData/${id}`, insight)).data
	}

	public async insightById (request: GetInsightByIdRequest): Promise<CalculatedResults> {
		const {
			insightId,
			cancelTokenSource
		} = request

		return (await this.axios.post(`/insights/${insightId}`,
			request.payload,
			{
				params: {
					...request.params,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
				},
				cancelToken: cancelTokenSource.token
			}
		)).data
	}

	public async insightTopValue (id: string, orderBy: string): Promise<any> {
		return (await this.axios.post(`/insights/topValue/${id}`, {}, {
			params: {
				orderBy
			}
		})).data
	}

	public async insightSave (insightQuery: Insight): Promise<string> {
		return (await this.axios.post("/insights/save", insightQuery)).data
	}
	public async insightDelete (insightId: string): Promise<void> {
		await this.axios.delete(`/insights/${insightId}`)
	}

	public async insightCasesForTable (request: GetInsightByIdRequest): Promise<CalculationResults> {
		const {
			insightId,
			cancelTokenSource
		} = request

		const payload = {
			...request.payload,
			pageNumber: request.payload.page
		}

		return (await this.axios.post(`/insights/tableData/${insightId}`,
			payload,
			{
				params: {
					...request.params,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
				},
				cancelToken: cancelTokenSource.token
			})).data
	}

	public async insightMarkAsCacheExpired (insightId: string): Promise<void> {
		await this.axios.post(`/insights/markAsCacheExpired/${insightId}`)
	}

	public async insightParameters (): Promise<DynamicParameter[]> {
		return (await this.axios.get("/insightParameters")).data
	}

	public async insightParameterSave (parameter: DynamicParameter): Promise<DynamicParameter> {
		return (await this.axios.post("/insightParameters/save", parameter)).data
	}
	public async insightParameterDelete (parameter: DynamicParameter): Promise<void> {
		await this.axios.delete(`/insightParameters/${parameter.key}`)
	}

	// ToDo: Not duplicate this from api_Tag!
	public async tagList (): Promise<Tag[]> { return (await this.axios.get("/tags")).data }

	public emailExists: (email: string) => Promise<boolean> = async (email) => (await this.axios.get(`/users/emailExists/${email}`)).data

	public async deleteSchedule (schedule: Schedule): Promise<void> {
		await this.axios.delete(`/schedule/${schedule.id}`)
	}

	public async createSchedule (schedule: Schedule): Promise<any> {
		await this.axios.post("/schedule/", schedule)
	}

	public async createCadenceSchedule (caseId: string, timeZone: string) {
		await this.axios.post("/schedule/cadence/", { caseId: caseId, timeZone: timeZone })
	}

	public async deleteScheduleQueue (schedule: ScheduleQueue): Promise<void> {
		await this.axios.delete(`/schedule/scheduleQueue/${schedule.id}`)
	}

	public async updateSchedule (schedule: Schedule): Promise<any> {
		await this.axios.put("/schedule/", schedule)
	}

	public async retrieveSchedules (): Promise<Schedule[]> {
		const { data } = await this.axios.get("/schedule/")
		return data
	}

	public async scheduleInstance (scheduleId: string, date: Date): Promise<{ scheduleInstance: CaseDetails, updatedScheduleInstance: CaseDetails }> {
		return (await this.axios.get("/schedule/instance", {
			params: {
				scheduleId,
				date: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
			}
		})).data
	}

	public async scheduleInstanceSet (scheduleId: string, date: Date, fieldValues: any): Promise<{ updatedScheduleInstance: CaseDetails }> {
		return (await this.axios.put("/schedule/instance", {
			scheduleId,
			date: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
			fieldValues
		})).data
	}

	public async retrieveCadenceSchedules (): Promise<any> {
		const { data } = await this.axios.get("/schedule/cadence")
		return data
	}

	public async generateCaseFromSchedule (schedule: Schedule): Promise<CaseDetails[]> {
		return (await this.axios.post("/schedule/GenerateCase", schedule)).data
	}
	public async createAccount (signupForm: SignUpForm): Promise<any> {
		await this.axios.post("/tenant/CreateNew/", signupForm)
	}

	public async tenantBrandingGet (): Promise<string> {
		return (await this.axios.get("/tenant/css")).data
	}

	public async tenantBrandingPost (css: string): Promise<string> {
		return (await this.axios.post("/tenant/css", {
			css
		})).data
	}

	public async generateQrCode (url: string): Promise<any> {
		return (await this.axios.post("/cases/QRCode/", `"${url}"`)).data
	}

	public async timeZonesGet (): Promise<TimeZone[]> {
		return (await this.axios.get("/schedule/timezones")).data
	}

	public async uploadFile (url: string, file: File): Promise<void> {
		const uploadFormData = new FormData()
		uploadFormData.append("file", file)

		return new Promise((resolve, reject) => {
			var xhr = new XMLHttpRequest()
			xhr.withCredentials = true

			xhr.open("POST", url, true)
			this.setXsrfHeader(xhr)

			xhr.onreadystatechange = () => {
				if (xhr.readyState === XHRReadyState.Done) {
					if (xhr.status < HttpStatusCode.BadRequest) {
						resolve()
						// Every thing ok, file uploaded
						console.log(xhr.responseText) // handle response.
					} else {
						if (xhr.responseText) {
							reject(xhr.responseText)
						} else {
							reject(xhr.status.toString())
						}
					}
				}
			}
			xhr.upload.onerror = (e: ProgressEvent) => {
				reject(new Error("Failed to upload file"))
			}

			xhr.send(uploadFormData)
		})
	}

	public async getOpsZendeskHelpWidgetEnabledSetting () {
		return (await this.axios.get("/features/GetOpsZendeskHelpWidgetEnabled")).data
	}

	public async getMfaRedirectUrl (): Promise<string> {
		const { data } = await this.axios.get("login/SuiteMFARedirectUrl")
		return data
	}

	public async sendCaseEmail (caseId:string, payload:CaseEmailRequest) : Promise<void> {
		await this.axios.post(`cases/email/${caseId}`, payload)
	}

	public async getContactsForCase (caseId:string) : Promise<CaseEmailContact[]> {
		const { data } = await this.axios.get(`/cases/users/${caseId}`)
		return data
	}

	public async reopenCase (caseId:string) : Promise<void> {
		await this.axios.put(`/cases/reopen/${caseId}`)
	}

	private setXsrfHeader (xhr: XMLHttpRequest): void {
		const xsrfValue = getCookieValue("XSRF-TOKEN")
		if (xsrfValue) {
			xhr.setRequestHeader("x-xsrf-token", xsrfValue)
		}
	}
}

const api = new Api()
export default api
